import { jsPDF } from 'jspdf';
import { CARD_BRANDS, QuoteRequest, RateOffer } from '../services/quoteService';

const loadLogo = async (): Promise<string | null> => {
  try {
    const response = await fetch('/logo.png');
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const rateStatusLabel = (status?: RateOffer['status']) => {
  switch (status) {
    case 'accepted':
      return 'Accepted';
    case 'declined':
      return 'Declined';
    case 'revision_requested':
      return 'New rate requested';
    case 'superseded':
      return 'Replaced';
    case 'offered':
      return 'Offered';
    default:
      return 'On file';
  }
};

export const downloadRatePdf = async (quote: QuoteRequest, offer: RateOffer, version: number) => {
  const pdf = new jsPDF({ unit: 'pt', format: 'letter' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const logo = await loadLogo();

  if (logo) {
    pdf.addImage(logo, 'PNG', 48, 36, 92, 36);
  } else {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(22);
    pdf.text('Simpl.', 48, 58);
  }

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(90);
  pdf.text('PROCESSING', logo ? 148 : 118, 68);

  pdf.setTextColor(20);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(18);
  pdf.text('Store Processing Rate', 48, 110);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(80);
  pdf.text(`Version ${version}  ·  ${rateStatusLabel(offer.status)}`, 48, 128);

  const offered = offer.offeredAt ? offer.offeredAt.toLocaleDateString() : '—';
  const rows = [
    ['Store', quote.businessName || '—'],
    ['Rate type', offer.type === 'ic_plus' ? 'I/C plus' : 'Fixed'],
    ['Effective', offered],
    ['Monthly volume', quote.monthlyVolume || '—'],
    ['Contact', quote.email || quote.customerEmail || '—'],
  ];

  let y = 156;
  rows.forEach(([label, value]) => {
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(120);
    pdf.setFontSize(9);
    pdf.text(label, 48, y);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(20);
    pdf.setFontSize(11);
    pdf.text(String(value), 170, y);
    y += 18;
  });

  y += 16;
  pdf.setFillColor(18, 18, 18);
  pdf.rect(48, y, pageWidth - 96, 28, 'F');
  pdf.setTextColor(255);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  pdf.text('Card type', 64, y + 18);
  pdf.text('% Rate', 280, y + 18);
  pdf.text('$ / transaction', 400, y + 18);

  y += 28;
  CARD_BRANDS.forEach((brand, index) => {
    const card = offer.cards?.[brand.id];
    if (index % 2 === 0) {
      pdf.setFillColor(246, 247, 249);
      pdf.rect(48, y, pageWidth - 96, 28, 'F');
    }
    pdf.setTextColor(20);
    pdf.setFont('helvetica', 'bold');
    pdf.text(brand.label, 64, y + 18);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`${card?.percent ?? 0}%`, 280, y + 18);
    pdf.text(`$${(card?.fee ?? 0).toFixed(2)}`, 400, y + 18);
    y += 28;
  });

  y += 36;
  pdf.setDrawColor(220);
  pdf.line(48, y, pageWidth - 48, y);
  y += 24;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.setTextColor(20);
  pdf.text('Simpl Processing', 48, y);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(90);
  pdf.text('(201) 846-0591  ·  simran@simplprocessing.com', 48, y + 16);
  pdf.text('Keep this copy for your records.', 48, y + 32);

  const safeName = (quote.businessName || 'customer').replace(/[^a-z0-9]+/gi, '-');
  pdf.save(`Simpl-Rate-${safeName}-v${version}.pdf`);
};
